import type { DataSourceDef } from '../graph/dataGraph.js';

export type DataSourceStatus = 'idle' | 'loading' | 'ready' | 'stale' | 'error' | 'closed';

export interface DataSourceSnapshot {
  sourceId: string;
  revision: number;
  schemaRevision: number;
  records: unknown;
  document?: unknown;
  /**
   * Mutations whose effects the records hold and whose news the reader may
   * not have had yet -- those of the deltas a refresh stood in for. Optional,
   * as in {@link DataSourceDelta.mutationIds}.
   */
  mutationIds?: string[];
}

export interface DataSourceDelta {
  sourceId: string;
  baseRevision: number;
  revision: number;
  upserts?: Record<string, unknown>;
  removes?: string[];
  changedFields?: string[];
  /**
   * The mutations whose effects this delta carries, by `mutationId` (the
   * engine's `DataSourceDelta::mutation_ids`). A reader holding one of them
   * pending drops it here, whichever of this delta and the acceptance comes
   * first: replayed over records that already hold it, a splice would be put
   * in twice. Optional: a source that names none is settled by revision, the
   * pending mutation dropped once the confirmed records reach the revision it
   * was accepted at.
   */
  mutationIds?: string[];
  /**
   * The operations of the mutations this delta carries, as the source applied
   * them -- a splice brought forward over what was applied to its text since
   * its writer's base -- in the source's order (the engine's
   * `DataSourceDelta::applied`). The engine's store brings the reader's own
   * pending splices forward over another writer's, so they stay where they
   * were typed. Optional: a source that sends none leaves them as sent.
   */
  applied?: DataAppliedOperations[];
}

/** One mutation a delta carries, with its operations as the source applied them. */
export interface DataAppliedOperations {
  mutationId: string;
  operations: DataMutationOperation[];
}

export type DataRecordKey = boolean | number | string;

/**
 * One change a mutation asks of a source, as the engine's `DataOperation`
 * serializes it. `spliceField` writes what was done to a text, not what it
 * became: `delete` characters taken out at `at` and `insert` put there.
 * Positions count characters -- Unicode code points, as Rust's `char` does --
 * never UTF-16 units, so an emoji or a CJK character beyond the Basic
 * Multilingual Plane is one position.
 */
export type DataMutationOperation =
  | { kind: 'setField'; key: DataRecordKey; field: string; value: unknown }
  | { kind: 'upsert'; key: DataRecordKey; record: unknown }
  | { kind: 'remove'; key: DataRecordKey }
  | { kind: 'spliceField'; key: DataRecordKey; field: string; at: number; delete: number; insert: string };

export const DATA_MUTATION_OPERATION_KINDS: readonly DataMutationOperation['kind'][] = ['setField', 'upsert', 'remove', 'spliceField'];

/**
 * `text` with `remove` characters taken out at `at` and `insert` put there,
 * and the characters taken out; `null` where the splice runs past the end.
 * Positions count code points (the engine's `splice_text`).
 */
export function spliceText(text: string, at: number, remove: number, insert: string): { text: string; deleted: string } | null {
  const characters = Array.from(text);
  if (!Number.isSafeInteger(at) || !Number.isSafeInteger(remove) || at < 0 || remove < 0
    || at > characters.length || remove > characters.length - at) {
    return null;
  }
  return {
    text: characters.slice(0, at).join('') + insert + characters.slice(at + remove).join(''),
    deleted: characters.slice(at, at + remove).join(''),
  };
}

export interface DataMutationRequest {
  /** `mut_` and a UUIDv7, as the engine mints it (kb:decision/14). */
  mutationId: string;
  /**
   * The confirmed revision the mutation was made against. Informational: a
   * source applies mutations in the order they arrive and does not refuse one
   * for being behind (kb:decision/53).
   */
  baseRevision: number;
  operations: DataMutationOperation[];
}

export interface DataMutationResult {
  mutationId: string;
  revision: number;
}

export class DataMutationRejectedError extends Error {
  readonly code?: string;
  readonly currentRevision?: number;

  constructor(message: string, options: { code?: string; currentRevision?: number } = {}) {
    super(message);
    this.name = 'DataMutationRejectedError';
    this.code = options.code;
    this.currentRevision = options.currentRevision;
  }
}

export class DataAuthenticationError extends Error {
  readonly code: string;

  constructor(message: string, code = 'authentication_failed') {
    super(message);
    this.name = 'DataAuthenticationError';
    this.code = code;
  }
}

export interface CredentialRequest {
  reference: string;
  sourceId: string;
  provider: string;
  resourceUrl: string;
  origin: string;
}

interface OriginBoundCredential {
  /** Exact endpoint origins this credential may be disclosed to. */
  allowedOrigins: readonly string[];
}

export interface HttpCredentialMaterial extends OriginBoundCredential {
  kind: 'http-headers';
  headers: Readonly<Record<string, string>>;
}

export interface WebSocketCredentialMaterial extends OriginBoundCredential {
  kind: 'websocket-bearer';
  token: string;
}

export type CredentialMaterial = HttpCredentialMaterial | WebSocketCredentialMaterial;
export type CredentialResolver = (request: CredentialRequest) => Promise<CredentialMaterial | null>;

export interface DataSourceAccess {
  subject: string;
  /** Unix epoch milliseconds. */
  expiresAt: number;
  permissions: {
    read: boolean;
    operations: readonly DataMutationOperation['kind'][];
    grants: readonly DataSourcePermissionGrant[];
  };
}

export interface DataSourcePermissionGrant {
  operations: readonly DataMutationOperation['kind'][];
  fields?: readonly string[];
  records?: readonly string[];
}

export interface DataProviderContext {
  sourceId: string;
  baseUrl?: string;
  signal: AbortSignal;
  resolveCredential: CredentialResolver;
}

export interface DataSourceSession {
  snapshot(): Promise<DataSourceSnapshot>;
  subscribe?(listener: (delta: DataSourceDelta) => void): () => void;
  access?(): DataSourceAccess | null;
  onFailure?(listener: (error: Error) => void): () => void;
  refresh?(): Promise<DataSourceSnapshot>;
  mutate?(request: DataMutationRequest): Promise<DataMutationResult>;
  close(): void;
}

export interface DataSourceProvider {
  readonly capabilities?: DataProviderCapabilities;
  connect(source: DataSourceDef, context: DataProviderContext): Promise<DataSourceSession>;
}

export interface DataProviderCapabilities {
  snapshots: boolean;
  deltas: boolean;
  refresh: boolean;
  streaming: boolean;
  credentials: boolean;
  mutations: boolean;
}

export class DataProviderRegistry {
  private readonly providers = new Map<string, DataSourceProvider>();

  register(kind: string, provider: DataSourceProvider): () => void {
    if (!kind) throw new Error('Data provider kind cannot be empty');
    if (this.providers.has(kind)) throw new Error(`Data provider '${kind}' is already registered`);
    this.providers.set(kind, provider);
    return () => this.providers.delete(kind);
  }

  get(kind: string): DataSourceProvider {
    const provider = this.providers.get(kind);
    if (!provider) throw new Error(`Unknown data provider '${kind}'`);
    return provider;
  }

  capabilities(kind: string): DataProviderCapabilities | undefined { return this.providers.get(kind)?.capabilities; }
}

class StaticSession implements DataSourceSession {
  constructor(private current: DataSourceSnapshot, private readonly reload?: () => Promise<DataSourceSnapshot>) {}
  snapshot(): Promise<DataSourceSnapshot> { return Promise.resolve(this.current); }
  async refresh(): Promise<DataSourceSnapshot> {
    if (this.reload) this.current = await this.reload();
    return this.current;
  }
  close(): void {}
}

function resolveResourceUrl(resource: string, baseUrl?: string): string | URL {
  const pageUrl = typeof location === 'undefined' ? undefined : location.href;
  if (!baseUrl) return pageUrl ? new URL(resource, pageUrl) : resource;
  const absoluteBase = pageUrl ? new URL(baseUrl, pageUrl).toString() : baseUrl;
  return new URL(resource, absoluteBase);
}

function resourceOrigin(resource: string | URL, sourceId: string): string {
  try {
    const url = new URL(String(resource));
    if (!['http:', 'https:', 'ws:', 'wss:'].includes(url.protocol) || url.origin === 'null') throw new Error();
    return url.origin;
  } catch {
    throw new DataAuthenticationError(
      `Credentialed data source '${sourceId}' must resolve to an absolute URL`,
      'invalid_resource',
    );
  }
}

function normalizeAllowedOrigin(value: string, sourceId: string): string {
  try {
    const url = new URL(value);
    if (!['http:', 'https:', 'ws:', 'wss:'].includes(url.protocol) || url.origin === 'null') throw new Error();
    return url.origin;
  } catch {
    throw new DataAuthenticationError(
      `Credential for data source '${sourceId}' contains an invalid allowed origin`,
      'invalid_credential',
    );
  }
}

async function resolveOriginBoundCredential(
  source: DataSourceDef,
  context: DataProviderContext,
  resource: string | URL,
): Promise<CredentialMaterial | null> {
  if (!source.credential) return null;
  const origin = resourceOrigin(resource, context.sourceId);
  const credential = await context.resolveCredential({
    reference: source.credential,
    sourceId: context.sourceId,
    provider: source.provider,
    resourceUrl: String(resource),
    origin,
  });
  if (!credential) {
    throw new DataAuthenticationError(
      `Credential profile '${source.credential}' is unavailable for data source '${context.sourceId}'`,
      'missing_credential',
    );
  }
  const allowed = credential.allowedOrigins.some(value => normalizeAllowedOrigin(value, context.sourceId) === origin);
  if (!allowed) {
    throw new DataAuthenticationError(
      `Credential profile '${source.credential}' is not allowed for origin '${origin}'`,
      'origin_not_allowed',
    );
  }
  return credential;
}

export function createJsonProvider(fetchImpl: typeof fetch = fetch): DataSourceProvider {
  return {
    capabilities: { snapshots: true, deltas: false, refresh: true, streaming: false, credentials: true, mutations: false },
    async connect(source, context) {
      let revision = 0;
      const load = async (): Promise<DataSourceSnapshot> => {
        const resource = resolveResourceUrl(source.resource, context.baseUrl);
        const credential = await resolveOriginBoundCredential(source, context, resource);
        if (credential && credential.kind !== 'http-headers') {
          throw new DataAuthenticationError(
            `Credential profile '${source.credential}' cannot authenticate an HTTP data source`,
            'credential_kind_mismatch',
          );
        }
        const headers = credential?.headers ?? {};
        const response = await fetchImpl(resource, { headers, signal: context.signal });
        if (!response.ok) throw new Error(`Data source '${context.sourceId}' failed to load: ${response.status}`);
        const document: unknown = await response.json();
        return { sourceId: context.sourceId, revision: ++revision, schemaRevision: 1, records: document, document };
      };
      return new StaticSession(await load(), load);
    },
  };
}

export interface MemoryDataSource {
  snapshot(): unknown;
  /**
   * The deltas of the resource. One that carries a mutation `mutate` applied
   * names it in `mutationIds`: an in-memory source that tells the delta at
   * once has its writer hear it before the promise `mutate` gave resolves.
   */
  subscribe?(listener: (delta: Omit<DataSourceDelta, 'sourceId'>) => void): () => void;
  mutate?(request: DataMutationRequest): Promise<DataMutationResult>;
}

export class MemoryDataProvider implements DataSourceProvider {
  readonly capabilities = { snapshots: true, deltas: true, refresh: false, streaming: true, credentials: false, mutations: true } as const;
  private readonly resources = new Map<string, MemoryDataSource>();
  register(resource: string, source: MemoryDataSource): () => void {
    this.resources.set(resource, source);
    return () => this.resources.delete(resource);
  }
  async connect(source: DataSourceDef, context: DataProviderContext): Promise<DataSourceSession> {
    const memory = this.resources.get(source.resource);
    if (!memory) throw new Error(`Memory resource '${source.resource}' is not registered`);
    let revision = 1;
    let unsubscribe: (() => void) | undefined;
    const session: DataSourceSession = {
      snapshot: async () => ({ sourceId: context.sourceId, revision, schemaRevision: 1, records: memory.snapshot() }),
      subscribe: listener => {
        unsubscribe?.();
        unsubscribe = memory.subscribe?.(delta => {
          revision = delta.revision;
          listener({ ...delta, sourceId: context.sourceId });
        });
        return () => { unsubscribe?.(); unsubscribe = undefined; };
      },
      close: () => { unsubscribe?.(); unsubscribe = undefined; },
    };
    if (memory.mutate) session.mutate = request => memory.mutate!(request);
    return session;
  }
}

export function createRestProvider(fetchImpl: typeof fetch = fetch): DataSourceProvider {
  return createJsonProvider(fetchImpl);
}

/**
 * A project's data as the engine's `kv` provider reads it: collections of
 * records by key. A source `{"provider": "kv", "resource": "<collection>"}`
 * reads that collection; its `records.path` walks a JSON resource and means
 * nothing here.
 */
export type KvCollections = ReadonlyMap<string, Readonly<Record<string, unknown>>>;

/**
 * The collections a project seed holds -- the engine's `fluveria.seed.jsonl`,
 * a line `{"collection", "key", "record"}` each, as `defaultTemplateSeed()`
 * hands the starter's over.
 */
export function parseKvSeed(seed: string): KvCollections {
  const collections = new Map<string, Record<string, unknown>>();
  seed.split('\n').forEach((line, index) => {
    if (!line.trim()) return;
    const entry = JSON.parse(line) as { collection?: unknown; key?: unknown; record?: unknown };
    if (typeof entry.collection !== 'string' || typeof entry.key !== 'string' || entry.record === undefined) {
      throw new Error(`Seed line ${index + 1} is not {"collection", "key", "record"}`);
    }
    const records = collections.get(entry.collection) ?? {};
    records[entry.key] = entry.record;
    collections.set(entry.collection, records);
  });
  return collections;
}

/** A collection of `collections` as a source reads it: a copy, and nothing where there is none. */
export function readKvCollection(collections: KvCollections, collection: string): Record<string, unknown> {
  return structuredClone(collections.get(collection) ?? {}) as Record<string, unknown>;
}

/**
 * The `kv` provider over a store held in memory -- what the engine's editor
 * gives a document with no project, the starter among them: its seed, read.
 * Reads only: the web has no project store to write into, so a write is
 * refused here rather than kept for a session and lost.
 */
export function createKvProvider(collections: KvCollections): DataSourceProvider {
  return {
    capabilities: { snapshots: true, deltas: false, refresh: true, streaming: false, credentials: false, mutations: false },
    async connect(source, context) {
      let revision = 0;
      const load = async (): Promise<DataSourceSnapshot> => ({
        sourceId: context.sourceId,
        revision: ++revision,
        schemaRevision: 1,
        records: readKvCollection(collections, source.resource),
      });
      return new StaticSession(await load(), load);
    },
  };
}

const AUTHENTICATION_TIMEOUT_MS = 10_000;

function parseAccess(message: {
  subject?: unknown;
  expiresAt?: unknown;
  permissions?: unknown;
}, sourceId: string): DataSourceAccess {
  const permissions = message.permissions as Partial<DataSourceAccess['permissions']> | undefined;
  const operations = permissions?.operations;
  const knownOperations = new Set<DataMutationOperation['kind']>(DATA_MUTATION_OPERATION_KINDS);
  if (typeof message.subject !== 'string' || !message.subject
    || !Number.isSafeInteger(message.expiresAt) || Number(message.expiresAt) <= Date.now()
    || permissions?.read !== true
    || !Array.isArray(operations) || operations.some(value => !knownOperations.has(value))) {
    throw new DataAuthenticationError(
      `WebSocket data source '${sourceId}' received an invalid authentication grant`,
      'invalid_authentication_grant',
    );
  }
  if (!Array.isArray(permissions.grants)) {
    throw new DataAuthenticationError(
      `WebSocket data source '${sourceId}' received invalid access grants`,
      'invalid_authentication_grant',
    );
  }
  const grants = permissions.grants.map(value => parsePermissionGrant(value, sourceId, knownOperations));
  const grantedOperations = new Set(grants.flatMap(grant => grant.operations));
  if (operations.some(operation => !grantedOperations.has(operation))
    || [...grantedOperations].some(operation => !operations.includes(operation))) {
    throw new DataAuthenticationError(
      `WebSocket data source '${sourceId}' received inconsistent access grants`,
      'invalid_authentication_grant',
    );
  }
  return {
    subject: message.subject,
    expiresAt: Number(message.expiresAt),
    permissions: {
      read: true,
      operations: [...operations],
      grants,
    },
  };
}

function parsePermissionGrant(
  value: unknown,
  sourceId: string,
  knownOperations: ReadonlySet<DataMutationOperation['kind']>,
): DataSourcePermissionGrant {
  const grant = value as Partial<DataSourcePermissionGrant> | null;
  if (!grant || typeof grant !== 'object' || !Array.isArray(grant.operations) || !grant.operations.length
    || grant.operations.some(operation => !knownOperations.has(operation))) {
    throw new DataAuthenticationError(
      `WebSocket data source '${sourceId}' received an invalid permission grant`,
      'invalid_authentication_grant',
    );
  }
  const fields = grant.fields;
  const records = grant.records;
  if ((fields !== undefined && (!Array.isArray(fields) || fields.some(item => typeof item !== 'string')))
    || (records !== undefined && (!Array.isArray(records) || records.some(item => typeof item !== 'string')))) {
    throw new DataAuthenticationError(
      `WebSocket data source '${sourceId}' received invalid grant restrictions`,
      'invalid_authentication_grant',
    );
  }
  return {
    operations: [...grant.operations],
    ...(fields === undefined ? {} : { fields: [...fields] }),
    ...(records === undefined ? {} : { records: [...records] }),
  };
}

export function createWebSocketProvider(createSocket: (url: string) => WebSocket = url => new WebSocket(url)): DataSourceProvider {
  return {
    capabilities: { snapshots: true, deltas: true, refresh: false, streaming: true, credentials: true, mutations: true },
    async connect(source, context) {
      const url = String(resolveResourceUrl(source.resource, context.baseUrl));
      const socket = createSocket(url);
      const listeners = new Set<(delta: DataSourceDelta) => void>();
      const failureListeners = new Set<(error: Error) => void>();
      const pendingMutations = new Map<string, {
        resolve: (result: DataMutationResult) => void;
        reject: (error: Error) => void;
      }>();
      let current: DataSourceSnapshot | null = null;
      let access: DataSourceAccess | null = null;
      let phase: 'opening' | 'authenticating' | 'subscribing' | 'ready' = 'opening';
      let initialSettled = false;
      let closed = false;
      let terminalError: Error | null = null;
      let authenticationTimeout: ReturnType<typeof setTimeout> | undefined;
      let expiryTimeout: ReturnType<typeof setTimeout> | undefined;
      let resolveInitial!: (snapshot: DataSourceSnapshot) => void;
      let rejectInitial!: (error: Error) => void;
      const initial = new Promise<DataSourceSnapshot>((resolve, reject) => { resolveInitial = resolve; rejectInitial = reject; });

      const clearTimers = (): void => {
        if (authenticationTimeout !== undefined) clearTimeout(authenticationTimeout);
        if (expiryTimeout !== undefined) clearTimeout(expiryTimeout);
        authenticationTimeout = undefined;
        expiryTimeout = undefined;
      };
      const rejectPending = (error: Error): void => {
        for (const pending of pendingMutations.values()) pending.reject(error);
        pendingMutations.clear();
      };
      const rejectInitialOnce = (error: Error): void => {
        if (initialSettled) return;
        initialSettled = true;
        rejectInitial(error);
      };
      const fail = (error: Error): void => {
        if (terminalError) return;
        terminalError = error;
        clearTimers();
        rejectInitialOnce(error);
        rejectPending(error);
        failureListeners.forEach(listener => listener(error));
      };
      const send = (message: unknown): void => {
        if (closed || socket.readyState !== 1) throw new Error(`WebSocket data source '${context.sourceId}' is not open`);
        socket.send(JSON.stringify(message));
      };
      const closeWithFailure = (error: Error, code = 4400): void => {
        fail(error);
        if (closed) return;
        closed = true;
        socket.close(code, error.message.slice(0, 120));
      };

      socket.addEventListener('open', () => {
        void (async () => {
          try {
            const credential = await resolveOriginBoundCredential(source, context, url);
            if (credential) {
              if (credential.kind !== 'websocket-bearer' || !credential.token) {
                throw new DataAuthenticationError(
                  `Credential profile '${source.credential}' cannot authenticate a WebSocket data source`,
                  'credential_kind_mismatch',
                );
              }
              phase = 'authenticating';
              send({ type: 'authenticate', sourceId: context.sourceId, token: credential.token });
              authenticationTimeout = setTimeout(() => closeWithFailure(new DataAuthenticationError(
                `WebSocket data source '${context.sourceId}' authentication timed out`,
                'authentication_timeout',
              ), 4401), AUTHENTICATION_TIMEOUT_MS);
            } else {
              phase = 'subscribing';
              send({ type: 'subscribe', sourceId: context.sourceId });
            }
          } catch (error) {
            closeWithFailure(error instanceof Error ? error : new Error(String(error)), 4401);
          }
        })();
      });
      socket.addEventListener('message', event => {
        try {
          const message = JSON.parse(String(event.data)) as
            | { type: 'authenticated'; sourceId: string; subject?: unknown; expiresAt?: unknown; permissions?: unknown }
            | { type: 'authenticationRejected' | 'accessRejected'; sourceId: string; code?: string; message?: string }
            | ({ type: 'snapshot' } & DataSourceSnapshot)
            | ({ type: 'delta' } & DataSourceDelta)
            | ({ type: 'mutationAccepted'; sourceId: string } & DataMutationResult)
            | { type: 'mutationRejected'; sourceId: string; mutationId: string; code?: string; message?: string; currentRevision?: number };
          if (message.sourceId !== context.sourceId) {
            throw new Error(`WebSocket data source '${context.sourceId}' received a message for '${message.sourceId}'`);
          }
          if (message.type === 'authenticated') {
            if (phase !== 'authenticating') {
              throw new DataAuthenticationError(
                `WebSocket data source '${context.sourceId}' authenticated in an invalid protocol state`,
                'protocol_error',
              );
            }
            access = parseAccess(message, context.sourceId);
            if (authenticationTimeout !== undefined) clearTimeout(authenticationTimeout);
            authenticationTimeout = undefined;
            const remainingMs = Math.max(0, access.expiresAt - Date.now());
            expiryTimeout = setTimeout(() => closeWithFailure(new DataAuthenticationError(
              `WebSocket data source '${context.sourceId}' credential expired`,
              'credential_expired',
            ), 4401), remainingMs);
            phase = 'subscribing';
            send({ type: 'subscribe', sourceId: context.sourceId });
          } else if (message.type === 'authenticationRejected' || message.type === 'accessRejected') {
            closeWithFailure(new DataAuthenticationError(
              message.message ?? `WebSocket data source '${context.sourceId}' access was rejected`,
              message.code ?? (message.type === 'authenticationRejected' ? 'authentication_rejected' : 'access_rejected'),
            ), message.type === 'authenticationRejected' ? 4401 : 4403);
          } else if (message.type === 'snapshot') {
            if (phase !== 'subscribing') {
              throw new Error(`WebSocket data source '${context.sourceId}' received a snapshot before subscribing`);
            }
            phase = 'ready';
            current = {
              sourceId: context.sourceId, revision: message.revision, schemaRevision: message.schemaRevision,
              records: message.records, document: message.document,
              ...(message.mutationIds ? { mutationIds: message.mutationIds } : {}),
            };
            if (!initialSettled) {
              initialSettled = true;
              resolveInitial(current);
            }
          } else if (message.type === 'delta') {
            listeners.forEach(listener => listener({ ...message, sourceId: context.sourceId }));
          } else if (message.type === 'mutationAccepted') {
            const pending = pendingMutations.get(message.mutationId);
            if (!pending) return;
            pendingMutations.delete(message.mutationId);
            pending.resolve({ mutationId: message.mutationId, revision: message.revision });
          } else if (message.type === 'mutationRejected') {
            const pending = pendingMutations.get(message.mutationId);
            if (!pending) return;
            pendingMutations.delete(message.mutationId);
            pending.reject(new DataMutationRejectedError(
              message.message ?? `Data mutation '${message.mutationId}' was rejected`,
              { code: message.code, currentRevision: message.currentRevision },
            ));
          } else {
            throw new Error(`WebSocket data source '${context.sourceId}' received an unknown message`);
          }
        } catch (error) {
          fail(error instanceof Error ? error : new Error(String(error)));
          socket.close();
        }
      });
      socket.addEventListener('error', () => {
        if (closed) return;
        closeWithFailure(new Error(`WebSocket data source '${context.sourceId}' failed`));
      });
      socket.addEventListener('close', event => {
        if (closed && terminalError) return;
        closed = true;
        const reason = event.reason || `WebSocket data source '${context.sourceId}' closed`;
        if (event.code === 4401 || event.code === 4403) {
          fail(new DataAuthenticationError(reason, event.code === 4401 ? 'authentication_rejected' : 'access_rejected'));
        } else {
          fail(new Error(reason));
        }
      });
      const abort = (): void => {
        closed = true;
        listeners.clear();
        failureListeners.clear();
        fail(new Error(`WebSocket data source '${context.sourceId}' was aborted`));
        socket.close();
      };
      context.signal.addEventListener('abort', abort, { once: true });
      if (context.signal.aborted) abort();

      return {
        snapshot: () => current ? Promise.resolve(current) : initial,
        subscribe: listener => { listeners.add(listener); return () => listeners.delete(listener); },
        access: () => access,
        onFailure: listener => { failureListeners.add(listener); return () => failureListeners.delete(listener); },
        mutate: request => {
          if (phase !== 'ready') {
            return Promise.reject(new Error(`WebSocket data source '${context.sourceId}' is not ready for mutation`));
          }
          const deniedOperation = access && request.operations.find(operation => !access!.permissions.operations.includes(operation.kind));
          if (deniedOperation) {
            return Promise.reject(new DataMutationRejectedError(
              `Data source '${context.sourceId}' does not allow '${deniedOperation.kind}' mutations for the current user`,
              { code: 'forbidden' },
            ));
          }
          if (pendingMutations.has(request.mutationId)) {
            return Promise.reject(new Error(`Data mutation '${request.mutationId}' is already pending`));
          }
          return new Promise<DataMutationResult>((resolve, reject) => {
            pendingMutations.set(request.mutationId, { resolve, reject });
            try {
              send({ type: 'mutate', sourceId: context.sourceId, ...request });
            } catch (error) {
              pendingMutations.delete(request.mutationId);
              reject(error instanceof Error ? error : new Error(String(error)));
            }
          });
        },
        close: () => {
          if (closed) return;
          closed = true;
          listeners.clear();
          failureListeners.clear();
          fail(new Error(`WebSocket data source '${context.sourceId}' closed`));
          socket.close();
        },
      };
    },
  };
}

export function createDefaultProviderRegistry(projectData: KvCollections = new Map()): { registry: DataProviderRegistry; memory: MemoryDataProvider } {
  const registry = new DataProviderRegistry();
  const memory = new MemoryDataProvider();
  registry.register('json', createJsonProvider());
  registry.register('rest', createRestProvider());
  registry.register('memory', memory);
  registry.register('websocket', createWebSocketProvider());
  registry.register('kv', createKvProvider(projectData));
  return { registry, memory };
}
